import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  clampFontSizePx,
  MAX_FONT_SIZE_PX,
  MIN_FONT_SIZE_PX,
  parseFontSizePx,
} from "../../types/canvasNodeData";
import { canvasToolbarButtonVariants } from "./canvasToolbarStyles";

export interface CanvasFontSizePreset {
  name: string;
  value: string;
}

/** The one preset ladder every canvas text surface offers. */
export const CANVAS_FONT_SIZE_PRESETS: CanvasFontSizePreset[] = [
  { name: "Small", value: "12px" },
  { name: "Medium", value: "16px" },
  { name: "Large", value: "22px" },
  { name: "Extra large", value: "32px" },
  { name: "Huge", value: "48px" },
];

interface CanvasToolbarFontSizeMenuProps {
  fontSize: string;
  presets?: CanvasFontSizePreset[];
  onFontSizeChange: (fontSize: string) => void;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Font size control shared by every canvas text surface (labels, notes,
 * shape text). Presets are shortcuts, not the value space: the numeric field
 * always shows the live px and any size in range applies as it is typed or
 * stepped, so the field and the rendered text can never disagree.
 */
export function CanvasToolbarFontSizeMenu({
  fontSize,
  presets = CANVAS_FONT_SIZE_PRESETS,
  onFontSizeChange,
  onOpenChange,
}: CanvasToolbarFontSizeMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const currentPx = parseFontSizePx(fontSize);
  const [draft, setDraft] = useState(String(currentPx));

  // Track the live value so a preset click, an arrow step, or an edit from
  // another surface all land in the field.
  useEffect(() => setDraft(String(currentPx)), [currentPx]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  // Applies on every keystroke and arrow step; an out-of-range draft ("1" on
  // the way to "120") stays in the field without being applied.
  const handleDraftChange = useCallback(
    (next: string) => {
      setDraft(next);
      const px = Number(next);
      if (!Number.isFinite(px) || px < MIN_FONT_SIZE_PX) return;
      onFontSizeChange(`${clampFontSizePx(px)}px`);
    },
    [onFontSizeChange],
  );

  const currentPreset = presets.find((p) => p.value === fontSize);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(canvasToolbarButtonVariants({ hasDropdown: true }))}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-xs font-medium">
                {currentPreset?.name ?? `${currentPx}px`}
              </span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Font size</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-40 p-1"
        align="center"
        side="top"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
      >
        {presets.map((size) => (
          <button
            key={size.value}
            onClick={() => onFontSizeChange(size.value)}
            className={cn(
              "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors whitespace-nowrap",
              "hover:bg-n-95/8 dark:hover:bg-n-95/10",
              currentPreset?.value === size.value &&
                "bg-primary/10 text-primary font-medium",
            )}
          >
            {size.name}
          </button>
        ))}
        <div className="pt-1 mt-1 border-t">
          <input
            type="number"
            aria-label="Font size in pixels"
            min={MIN_FONT_SIZE_PX}
            max={MAX_FONT_SIZE_PX}
            value={draft}
            onChange={(e) => handleDraftChange(e.target.value)}
            onBlur={() => setDraft(String(currentPx))}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleOpenChange(false);
              e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-0 border outline-none focus:ring-2 focus:ring-canvas-selection"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
